import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Popconfirm, Card, Select, Tag, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CarOutlined, StarFilled } from '@ant-design/icons';
import { 
  fetchBrands, 
  fetchModels, 
  createBrand, 
  updateBrand, 
  deleteBrand, 
  createModel, 
  updateModel, 
  deleteModel,
  type BrandDto,
  type ModelDto
} from '../services/vehicleService';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';

interface BrandGroup {
  brand: BrandDto;
  models: ModelDto[];
}

const Vehicles: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [allModels, setAllModels] = useState<ModelDto[]>([]);
  const [modelsForBrand, setModelsForBrand] = useState<ModelDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [brandModalVisible, setBrandModalVisible] = useState(false);
  const [modelModalVisible, setModelModalVisible] = useState(false);
  const [editingBrand, setEditingBrand] = useState<BrandDto | null>(null);
  const [editingModel, setEditingModel] = useState<ModelDto | null>(null);
  const [brandForm] = Form.useForm();
  const [modelForm] = Form.useForm();
  const [selectedBrandIdForModel, setSelectedBrandIdForModel] = useState<number | undefined>(undefined);
  
  useEffect(() => {
    loadBrands();
    loadAllModels();
  }, []);

  // Marka seçildiğinde model listesini yükle
  useEffect(() => {
    if (selectedBrandIdForModel) {
      loadModelsForBrand(selectedBrandIdForModel);
    } else {
      setModelsForBrand([]);
    }
  }, [selectedBrandIdForModel]);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const data = await fetchBrands();
      setBrands(data);
    } catch (error) {
      message.error('Markalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadAllModels = async () => {
    try {
      const data = await fetchModels();
      setAllModels(data);
    } catch (error) {
      console.error('Modeller yüklenirken hata:', error);
    }
  };

  const loadModelsForBrand = async (brandId: number) => {
    try {
      const data = await fetchModels(brandId);
      setModelsForBrand(data);
    } catch (error) {
      console.error('Modeller yüklenirken hata:', error);
    }
  };

  const handleAddBrand = () => {
    setEditingBrand(null);
    brandForm.resetFields();
    brandForm.setFieldsValue({ isFavourite: false });
    setBrandModalVisible(true);
  };

  const handleEditBrand = (brand: BrandDto) => {
    setEditingBrand(brand);
    brandForm.setFieldsValue({
      name: brand.name,
      isFavourite: brand.isFavourite
    });
    setBrandModalVisible(true);
  };

  const handleAddModel = () => {
    if (brands.length === 0) {
      message.warning('Önce bir marka eklemeniz gerekiyor');
      return;
    }
    setEditingModel(null);
    modelForm.resetFields();
    setSelectedBrandIdForModel(undefined);
    setModelsForBrand([]);
    modelForm.setFieldsValue({ isFavourite: false });
    setModelModalVisible(true);
  };

  const handleEditModel = (model: ModelDto) => {
    setEditingModel(model);
    setSelectedBrandIdForModel(model.brandId);
    modelForm.setFieldsValue({
      brandId: model.brandId,
      name: model.name,
      isFavourite: model.isFavourite
    });
    loadModelsForBrand(model.brandId);
    setModelModalVisible(true);
  };

  const handleSaveBrand = async () => {
    try {
      const values = await brandForm.validateFields();
      
      if (editingBrand) {
        await updateBrand(editingBrand.id, {
          name: values.name,
          isFavourite: values.isFavourite
        });
        message.success('Marka güncellendi');
      } else {
        await createBrand({
          name: values.name,
          isFavourite: values.isFavourite
        });
        message.success('Marka eklendi');
      }
      
      setBrandModalVisible(false);
      brandForm.resetFields();
      loadBrands();
      loadAllModels();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const handleSaveModel = async () => {
    try {
      const values = await modelForm.validateFields();
      
      if (editingModel) {
        await updateModel(editingModel.id, {
          name: values.name,
          isFavourite: values.isFavourite
        });
        message.success('Model güncellendi');
      } else {
        if (!values.brandId) {
          message.error('Lütfen marka seçin');
          return;
        }
        if (!values.name || !values.name.trim()) {
          message.error('Lütfen model adı girin');
          return;
        }
        await createModel({
          brandId: values.brandId,
          name: values.name.trim(),
          isFavourite: values.isFavourite || false
        });
        message.success('Model eklendi');
      }
      
      setModelModalVisible(false);
      modelForm.resetFields();
      setSelectedBrandIdForModel(undefined);
      setModelsForBrand([]);
      setEditingModel(null);
      loadBrands();
      loadAllModels();
    } catch (error: any) {
      console.error('Model kaydetme hatası:', error);
      // Validation hatası kontrolü
      if (error.errorFields && error.errorFields.length > 0) {
        const firstError = error.errorFields[0];
        if (firstError.errors && firstError.errors.length > 0) {
          message.error(firstError.errors[0]);
        } else {
          message.error('Form doğrulama hatası');
        }
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const handleDeleteModel = async (id: number) => {
    try {
      await deleteModel(id);
      message.success('Model silindi');
      loadBrands();
      loadAllModels();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Model silinirken hata oluştu');
      }
    }
  };

  const handleDeleteBrand = async (id: number) => {
    try {
      await deleteBrand(id);
      message.success('Marka silindi');
      loadBrands();
      loadAllModels();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Marka silinirken hata oluştu');
      }
    }
  };

  // Markalara göre grupla
  const getGroupedBrands = (): BrandGroup[] => {
    const grouped: { [key: number]: BrandGroup } = {};
    
    brands.forEach(brand => {
      grouped[brand.id] = {
        brand,
        models: []
      };
    });
    
    allModels.forEach(model => {
      if (grouped[model.brandId]) {
        grouped[model.brandId].models.push(model);
      }
    });
    
    return Object.values(grouped)
      .sort((a, b) => {
        // Favori markalar önce
        if (a.brand.isFavourite !== b.brand.isFavourite) {
          return a.brand.isFavourite ? -1 : 1;
        }
        return a.brand.name.localeCompare(b.brand.name);
      })
      .map(group => ({
        ...group,
        models: group.models.sort((a, b) => {
          // Favori modeller önce
          if (a.isFavourite !== b.isFavourite) {
            return a.isFavourite ? -1 : 1;
          }
          return a.name.localeCompare(b.name);
        })
      }));
  };

  const groupedBrands = getGroupedBrands();

  // Tablo için data source oluştur
  const tableData = groupedBrands.map((group) => ({
    key: `brand-${group.brand.id}`,
    brand: group.brand,
    isBrandRow: true,
    modelCount: group.models.length,
    models: group.models
  }));

  const columns = [
    {
      title: 'Marka',
      dataIndex: 'brand',
      key: 'brand',
      render: (brand: BrandDto, record: any) => {
        if (record.isBrandRow) {
          return (
            <Space>
              {brand.isFavourite && <StarFilled style={{ color: '#faad14' }} />}
              <strong style={{ fontSize: '16px' }}>{brand.name}</strong>
            </Space>
          );
        }
        return brand.name;
      },
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (text: string, record: any) => {
        if (record.isBrandRow) {
          return <span style={{ color: '#666' }}>{record.modelCount} model</span>;
        }
        return text;
      },
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: any) => {
        if (record.isBrandRow) {
          return (
            <Tag color={record.brand.isActive ? 'green' : 'red'}>
              {record.brand.isActive ? 'Aktif' : 'Pasif'}
            </Tag>
          );
        }
        return (
          <Tag color={isActive ? 'green' : 'red'}>
            {isActive ? 'Aktif' : 'Pasif'}
          </Tag>
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: any) => {
        if (record.isBrandRow) {
          return (
            <Space>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditBrand(record.brand)}
              >
                Düzenle
              </Button>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedBrandIdForModel(record.brand.id);
                  modelForm.setFieldsValue({ brandId: record.brand.id });
                  loadModelsForBrand(record.brand.id);
                  setEditingModel(null);
                  setModelModalVisible(true);
                }}
              >
                Model Ekle
              </Button>
              {canDelete && (
              <Popconfirm
                title={`"${record.brand.name}" markasını ve tüm modellerini silmek istediğinizden emin misiniz?`}
                onConfirm={() => handleDeleteBrand(record.brand.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                >
                  Sil
                </Button>
              </Popconfirm>
              )}
            </Space>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>
          <CarOutlined /> Marka ve Model Yönetimi
        </h1>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddBrand}
          >
            Marka Ekle
          </Button>
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={handleAddModel}
          >
            Model Ekle
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey="key"
          loading={loading}
          scroll={{ x: 400 }}
          expandable={{
            expandedRowRender: (record) => {
              if (!record.isBrandRow) return null;
              return (
                <Table
                  columns={[
                    {
                      title: 'Model',
                      dataIndex: 'name',
                      key: 'name',
                      render: (text: string, model: ModelDto) => (
                        <Space>
                          {model.isFavourite && <StarFilled style={{ color: '#faad14' }} />}
                          <span>{text}</span>
                        </Space>
                      ),
                    },
                    {
                      title: 'Görünen Ad',
                      dataIndex: 'displayName',
                      key: 'displayName',
                    },
                    {
                      title: 'Durum',
                      dataIndex: 'isActive',
                      key: 'isActive',
                      render: (isActive: boolean) => (
                        <Tag color={isActive ? 'green' : 'red'}>
                          {isActive ? 'Aktif' : 'Pasif'}
                        </Tag>
                      ),
                    },
                    {
                      title: 'İşlemler',
                      key: 'actions',
                      render: (_: any, model: ModelDto) => (
                        <Space>
                          <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditModel(model)}
                          />
                          {canDelete && (
                          <Popconfirm
                            title="Bu modeli silmek istediğinizden emin misiniz?"
                            onConfirm={() => handleDeleteModel(model.id)}
                            okText="Evet"
                            cancelText="Hayır"
                          >
                            <Button
                              type="primary"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                            />
                          </Popconfirm>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                  dataSource={record.models}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              );
            },
            rowExpandable: (record) => record.isBrandRow && record.modelCount > 0,
            defaultExpandAllRows: false,
          }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} marka`,
          }}
        />
      </Card>

      {/* Marka Ekleme/Düzenleme Modal */}
      <Modal
        title={editingBrand ? 'Markayı Düzenle' : 'Yeni Marka Ekle'}
        open={brandModalVisible}
        onOk={handleSaveBrand}
        onCancel={() => {
          setBrandModalVisible(false);
          brandForm.resetFields();
          setEditingBrand(null);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 500}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={brandForm} layout="vertical">
          <Form.Item
            name="name"
            label="Marka Adı"
            rules={[
              { 
                required: true, 
                message: 'Lütfen marka adı girin'
              }
            ]}
          >
            <Input
              placeholder="Marka adı girin (örn: Renault, Ford, Toyota)"
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="isFavourite"
            label="Favori"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Model Ekleme/Düzenleme Modal */}
      <Modal
        title={editingModel ? 'Modeli Düzenle' : 'Yeni Model Ekle'}
        open={modelModalVisible}
        onOk={handleSaveModel}
        onCancel={() => {
          setModelModalVisible(false);
          modelForm.resetFields();
          setSelectedBrandIdForModel(undefined);
          setModelsForBrand([]);
          setEditingModel(null);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={modelForm} layout="vertical">
          <Form.Item
            name="brandId"
            label="Marka"
            rules={[
              { required: true, message: 'Lütfen marka seçin' }
            ]}
          >
            <Select
              placeholder="Marka seçin"
              showSearch
              allowClear
              disabled={!!editingModel}
              onChange={(value) => {
                setSelectedBrandIdForModel(value);
                if (value) {
                  loadModelsForBrand(value);
                } else {
                  setModelsForBrand([]);
                }
              }}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={brands.map(brand => ({ value: brand.id, label: brand.name }))}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="Model Adı"
            rules={[
              { required: true, message: 'Lütfen model adı girin' }
            ]}
          >
            <Input
              placeholder="Model adı girin"
              list="models-list"
              allowClear
            />
          </Form.Item>
          <datalist id="models-list">
            {modelsForBrand.map(model => (
              <option key={model.id} value={model.name} />
            ))}
          </datalist>
          <Form.Item
            name="isFavourite"
            label="Favori"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Vehicles;
